import { logError } from '../../shared/utils/logger';
import {
    getTokens as readTokens,
    setTokens as persistTokens,
    clearTokens,
} from '../../shared/utils/secureTokenStorage';

// Organization context for multi-tenant API requests
let currentOrgId = null;

/**
 * Set the current organization context for API requests
 * This will add X-Org-Id header to all authenticated requests
 *
 * @param {string|null} orgId - The organization ID, or null to clear
 */
export const setOrgContext = (orgId) => {
    currentOrgId = orgId;
};

/**
 * Get the current organization context
 *
 * @returns {string|null} The current organization ID
 */
export const getOrgContext = () => {
    return currentOrgId;
};

/**
 * Clear the current organization context
 */
export const clearOrgContext = () => {
    currentOrgId = null;
};

// API Base URL configuration
// Priority:
//   1. REACT_APP_API_BASE env var (set this in Amplify / CI for deploys)
//   2. localhost dev → http://localhost:8000
//   3. cloudmortgage.io production → https://api.cloudmortgage.io (the actual API host)
//   4. Otherwise empty (same-origin proxy)
//
// The production fallback (step 3) was added 2026-05-23 after the Lab UI
// showed "Preview mode — /api/lab/* not reachable" despite the backend
// being deployed and responsive at api.cloudmortgage.io. The frontend at
// cloudmortgage.io was hitting same-origin /api/* which doesn't proxy
// to the API host, so fetch failed without a status — triggering the
// isBackendUnavailable preview banner.
const _hostname = typeof window !== "undefined" ? window.location.hostname : "";
const _productionFallback = (() => {
    if (_hostname === "localhost") return "http://localhost:8000";
    // Match cloudmortgage.io and any subdomain other than api.* (which IS the API host itself)
    if (/(^|\.)cloudmortgage\.io$/.test(_hostname) && !_hostname.startsWith("api.")) {
        return "https://api.cloudmortgage.io";
    }
    return "";
})();
const RAW_BASE_URL = process.env.REACT_APP_API_BASE || _productionFallback;
const normalizedBaseUrl = RAW_BASE_URL.replace(/\/+$/, "");
export const BASE_URL = normalizedBaseUrl;

// DEBUG: Log API configuration on startup
console.log('[API Client] Configuration:', {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_API_BASE: process.env.REACT_APP_API_BASE,
    BASE_URL: normalizedBaseUrl || '(empty - using proxy)'
});

// Global authentication error handler
let globalAuthErrorHandler = null;

/**
 * Register a global callback to handle authentication errors
 * This will be called when authentication permanently fails (e.g., token refresh fails)
 */
export const setGlobalAuthErrorHandler = (handler) => {
    globalAuthErrorHandler = handler;
};

/**
 * Clear the global authentication error handler
 */
export const clearGlobalAuthErrorHandler = () => {
    globalAuthErrorHandler = null;
};


export const getStoredTokens = () => readTokens();

// Backward compatibility helper
export const normalizeTokens = (tokenData) => {
    // Handle different backend response formats
    if (!tokenData) return null;

    // If tokenData is a string, assume it's the access token
    if (typeof tokenData === "string") {
        return { accessToken: tokenData };
    }

    // If tokenData already has accessToken, use as-is
    if (tokenData.accessToken) {
        return tokenData;
    }

    // If tokenData has 'token' property (nested structure)
    if (tokenData.token) {
        if (typeof tokenData.token === "string") {
            return { accessToken: tokenData.token, refreshToken: tokenData.refreshToken };
        }
        return tokenData.token;
    }

    // Fallback: assume the object itself contains the token data
    return tokenData;
};

/**
 * Build a uniformly-tagged "auth expired" error. The `authExpired` flag
 * lets downstream code (e.g. isBackendUnavailable in LabService) tell
 * an auth failure apart from a network/CORS error — both throw without
 * a `.status` field but mean very different things.
 *
 * Without this tag, refresh-token failures were misclassified as
 * "backend not deployed" and triggered the Lab UI's preview banner
 * instead of the proper "session expired → redirect to login" UX.
 */
function _authExpired() {
    const err = new Error("Authentication expired. Please sign in again.");
    err.authExpired = true;
    err.status = 401;
    return err;
}

export async function refreshAccessToken() {
    const tokens = readTokens();
    if (!tokens?.refreshToken) {
        clearTokens();
        throw _authExpired();
    }
    const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Try both camelCase and snake_case for backend compatibility
        body: JSON.stringify({
            refreshToken: tokens.refreshToken,
            refresh_token: tokens.refreshToken
        })
    });
    if (!response.ok) {
        clearTokens();
        throw _authExpired();
    }
    const data = await response.json();
    if (data?.token) {
        // SECURITY: Preserve storage location (session vs local) during refresh
        const normalized = normalizeTokens(data.token);
        persistTokens(normalized, false);
        return normalized;
    }
    throw new Error("Authentication expired. Please sign in again.");
}

export const getAuthHeaders = (extraHeaders = {}) => {
    const tokens = readTokens();
    const headers = { ...extraHeaders };

    if (tokens?.accessToken) {
        headers.Authorization = `Bearer ${tokens.accessToken}`;
    }

    // Include organization context header if set
    if (currentOrgId) {
        headers['X-Org-Id'] = currentOrgId;
    }

    return headers;
};

export async function fetchWithAuth(url, options = {}, retry = true) {
    const headers = getAuthHeaders(options.headers);
    const { skipGlobalAuthHandler, ...fetchOptions } = options;
    const response = await fetch(url, { ...fetchOptions, headers });

    if (response.status === 401 && retry) {
        try {
            await refreshAccessToken();
            return fetchWithAuth(url, { ...options, skipGlobalAuthHandler }, false);
        } catch (refreshError) {
            // Only log if not suppressing global auth errors (reduces noise for expected failures)
            if (!skipGlobalAuthHandler) {
                logError("Token refresh failed:", refreshError);
            }

            // Trigger global authentication error handler
            // Skip if caller explicitly opts out (e.g., during login pre-fetch)
            if (globalAuthErrorHandler && !skipGlobalAuthHandler) {
                console.error("Authentication expired globally. Redirecting to login...");
                // Call the handler asynchronously to avoid blocking
                setTimeout(() => {
                    globalAuthErrorHandler(refreshError);
                }, 100);
            }

            throw refreshError;
        }
    }

    if (!response.ok) {
        logError(`API request failed: ${response.status} ${response.statusText}`, url);
    }

    return response;
}
